import { useEffect, useState, createContext, useContext, useCallback } from "react";
import { api } from "./api";
import { getPaymentBranding } from "./paymentBranding";

/**
 * Public site settings (checkout_mode, program_status, price, etc.).
 * Source of truth lives in the backend Mongo `settings` collection and can be
 * changed live from the Admin Dashboard "Setări curs" tab — NO redeploy needed.
 */
const SettingsContext = createContext(null);

const DEFAULTS = {
  price: null,
  currency: "RON",
  checkout_mode: "registration_only",
  program_status: "in_accreditation",
  netopia_enabled: false,
  online_payment_ready: false,
  // M901 — active payment provider (Mongo > env > fallback "netopia").
  // The branding hook below picks the right copy/badges based on this slug.
  active_payment_provider: "netopia",
  active_payment_provider_display_name: "Netopia Payments",
  checkout_intro_text: "",
  success_text_paid: "",
  success_text_registration: "",
  // Brochure popup. False until the backend says otherwise, so it never
  // flashes in the seconds before settings load.
  lead_popup_enabled: false,
  // "contact" until the backend says otherwise — the brochure is never
  // promised on the strength of a default.
  lead_form_mode: "contact",
  _loaded: false,
};

/**
 * Public settings as the app reads them.
 *
 * One rule beyond "defaults, then the server": a server that does not send
 * `lead_form_mode` predates the contact form, and the only form it knows how
 * to accept is the brochure one — marketing consent required, phone optional.
 * Frontend and backend deploy independently (Vercel and Render, a few minutes
 * apart), so for that window the form has to follow the server it is actually
 * talking to rather than the default this build would prefer.
 */
export function mergePublicSettings(data) {
  const fromServer = data || {};
  return {
    ...DEFAULTS,
    ...("lead_form_mode" in fromServer ? {} : { lead_form_mode: "brochure" }),
    ...fromServer,
    _loaded: true,
  };
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULTS);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/settings/public");
      setSettings(mergePublicSettings(r.data));
    } catch {
      setSettings((s) => ({ ...s, _loaded: true }));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <SettingsContext.Provider value={{ ...settings, refresh }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    // Defensive: render-safe defaults if the provider isn't mounted.
    return { ...DEFAULTS, refresh: () => {} };
  }
  return ctx;
}

/**
 * Convenience helper — returns the active mode AS A USABLE BOOLEAN PAIR
 * so components can write: `const { isOnline, isRegistration } = useCheckoutMode()`.
 */
const PAYMENT_MODES = ["online_payment", "online_payment_no_group"];

export function useCheckoutMode() {
  const s = useSettings();
  const isOnline = PAYMENT_MODES.includes(s.checkout_mode) && s.online_payment_ready;
  return {
    mode: isOnline ? s.checkout_mode : "registration_only",
    isOnline,
    isRegistration: !isOnline,
    // `isOnline` stays true for BOTH paying modes, so anything that only asks
    // "do we take money here?" — the Hero and Pricing CTAs — needs no change.
    // What separates them is whether a cohort must be picked first: in
    // `online_payment_no_group` the participant states preferences instead and
    // the group is assigned later from the dashboard.
    requiresGroup: s.checkout_mode !== "online_payment_no_group",
    programStatus: s.program_status,
    loaded: s._loaded,
  };
}

/**
 * M901 — Returns the provider-aware UI branding for the CURRENTLY ACTIVE
 * payment provider (the one new purchases will use). Use this in
 * `Checkout`, `Footer`, `Success` etc. to render copy and trust badges
 * without hard-coding any provider name.
 *
 * Components that need the per-purchase provider (e.g. a Success page
 * polling a SPECIFIC order) should call `getPaymentBranding(provider)`
 * directly with the value returned from `/api/payments/status/{order_id}`.
 */
export function useActivePaymentBranding() {
  const s = useSettings();
  return getPaymentBranding(s.active_payment_provider);
}
